import type { TableCell, TableElement } from '@/components/canvas/document/publicTypes';
import {
  TABLE_BORDER_COLOR,
  TABLE_BORDER_WIDTH,
  TABLE_CELL_PADDING_DEFAULT,
} from '@/components/canvas/shared/constants/legacyConfig';
import {
  getRowHeights,
  getColWidths,
  getCellBackground,
  getCellTextStyle,
  getMergedInfo,
  resolveCellTextColor,
} from '@/components/canvas/elements/table/utils/tableUtilsIndex';
import { firstStroke } from '@/components/canvas/elements/model';
import { parseTextLineHeightToPx } from '@/components/canvas/elements/text/utils/textMeasure';
import { textDocumentToPlainText } from '@/components/canvas/elements/text/model/textPlain';
import { fixCjkLineStartsPullBack } from '@/components/canvas/elements/text/utils/cjkLineStart';

function fontCss(style: ReturnType<typeof getCellTextStyle>): string {
  return `${style.fontStyle} ${style.fontWeight} ${style.fontSize}px "${style.fontFamily}", sans-serif`;
}

function measureText(ctx: CanvasRenderingContext2D, text: string, letterSpacingPx?: number): number {
  if (!letterSpacingPx) return ctx.measureText(text).width;
  let width = 0;
  for (const ch of text) width += ctx.measureText(ch).width + letterSpacingPx;
  return width;
}

function drawText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, letterSpacingPx?: number): void {
  if (!letterSpacingPx) {
    ctx.fillText(text, x, y);
    return;
  }
  let curX = x;
  for (const ch of text) {
    ctx.fillText(ch, curX, y);
    curX += ctx.measureText(ch).width + letterSpacingPx;
  }
}

function wrapLine(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, letterSpacingPx?: number): string[] {
  if (!text) return [''];
  const lines: string[] = [];
  let line = '';
  for (const ch of text) {
    const next = line + ch;
    if (line && measureText(ctx, next, letterSpacingPx) > maxWidth) {
      lines.push(line);
      line = ch;
    } else {
      line = next;
    }
  }
  lines.push(line);
  return lines;
}

type WrappedCellLine = {
  text: string;
  isLastInParagraph: boolean;
};

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, letterSpacingPx?: number): WrappedCellLine[] {
  const explicitLines = text.split('\n');
  return explicitLines.flatMap((line) => {
    const wrapped = fixCjkLineStartsPullBack(wrapLine(ctx, line, maxWidth, letterSpacingPx));
    return wrapped.map((wrappedLine, index) => ({
      text: wrappedLine,
      isLastInParagraph: index === wrapped.length - 1,
    }));
  });
}

function drawJustifiedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  targetWidth: number,
  letterSpacingPx?: number,
): void {
  const chars = [...text];
  if (chars.length < 2) {
    drawText(ctx, text, x, y, letterSpacingPx);
    return;
  }

  const naturalWidth = measureText(ctx, text, letterSpacingPx);
  const extra = targetWidth - naturalWidth;
  if (extra <= 0.5) {
    drawText(ctx, text, x, y, letterSpacingPx);
    return;
  }

  const spaceIndices = chars
    .map((ch, index) => (/\s/.test(ch) ? index : -1))
    .filter((index) => index >= 0);
  const stretchSet = new Set(spaceIndices.length ? spaceIndices : chars.slice(0, -1).map((_, index) => index));
  const extraPerGap = extra / Math.max(1, stretchSet.size);

  let curX = x;
  chars.forEach((ch, index) => {
    ctx.fillText(ch, curX, y);
    curX += ctx.measureText(ch).width + (letterSpacingPx ?? 0);
    if (stretchSet.has(index)) curX += extraPerGap;
  });
}

function drawHorizontalCellText(
  ctx: CanvasRenderingContext2D,
  text: string,
  style: ReturnType<typeof getCellTextStyle>,
  rect: { x: number; y: number; width: number; height: number },
  padding: { top: number; right: number; bottom: number; left: number },
): void {
  const innerW = Math.max(0, rect.width - padding.left - padding.right);
  const innerH = Math.max(0, rect.height - padding.top - padding.bottom);
  if (innerW <= 0 || innerH <= 0) return;

  const letterSpacingPx = style.letterSpacing != null ? Number(style.letterSpacing) * style.fontSize : undefined;
  const lineHeight = parseTextLineHeightToPx(style.lineHeight, style.fontSize);
  const lines = wrapText(ctx, text, innerW, letterSpacingPx);
  const totalH = lines.length * lineHeight;
  const startY =
    style.verticalAlign === 'bottom'
      ? rect.y + rect.height - padding.bottom - totalH
      : style.verticalAlign === 'middle'
        ? rect.y + padding.top + (innerH - totalH) / 2
        : rect.y + padding.top;

  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  lines.forEach((line, index) => {
    const lineW = measureText(ctx, line.text, letterSpacingPx);
    const tx =
      style.textAlign === 'center'
        ? rect.x + padding.left + innerW / 2 - lineW / 2
        : style.textAlign === 'right'
          ? rect.x + rect.width - padding.right - lineW
          : rect.x + padding.left;
    const y = startY + index * lineHeight;
    if (style.textAlign === 'justify' && !line.isLastInParagraph) {
      drawJustifiedText(ctx, line.text, rect.x + padding.left, y, innerW, letterSpacingPx);
    } else {
      drawText(ctx, line.text, tx, y, letterSpacingPx);
    }
  });
}

function drawVerticalCellText(
  ctx: CanvasRenderingContext2D,
  text: string,
  style: ReturnType<typeof getCellTextStyle>,
  rect: { x: number; y: number; width: number; height: number },
  padding: { top: number; right: number; bottom: number; left: number },
): void {
  const innerW = Math.max(0, rect.width - padding.left - padding.right);
  const innerH = Math.max(0, rect.height - padding.top - padding.bottom);
  if (innerW <= 0 || innerH <= 0) return;

  const lineHeight = parseTextLineHeightToPx(style.lineHeight, style.fontSize);
  const maxPerColumn = Math.max(1, Math.floor(innerH / lineHeight));
  const columns: string[] = [];
  for (const part of text.split('\n')) {
    if (!part) {
      columns.push('');
      continue;
    }
    for (let i = 0; i < part.length; i += maxPerColumn) {
      columns.push(part.slice(i, i + maxPerColumn));
    }
  }
  const columnW = lineHeight;
  const totalW = columns.length * columnW;
  const startRight =
    style.verticalAlign === 'bottom'
      ? rect.x + rect.width - padding.right
      : style.verticalAlign === 'middle'
        ? rect.x + padding.left + (innerW + totalW) / 2
        : rect.x + padding.left + totalW;

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  columns.forEach((column, colIndex) => {
    const x = startRight - columnW * colIndex - columnW / 2;
    for (let i = 0; i < column.length; i++) {
      const y = rect.y + padding.top + i * lineHeight;
      ctx.fillText(column[i], x, y);
    }
  });
}

function cellText(cell: TableCell | undefined): string {
  return cell?.text?.document
    ? textDocumentToPlainText(cell.text.document)
    : cell?.text?.content ?? '';
}

export function drawTable(ctx: CanvasRenderingContext2D, el: TableElement) {
  const { x, y } = el.layout;
  const { width, height } = el.size;
  const rows = Math.max(1, el.data.rows);
  const cols = Math.max(1, el.data.cols);
  const rowHeights = getRowHeights(el);
  const colWidths = getColWidths(el);
  const padding = el.size.padding;
  const pt = padding?.top ?? TABLE_CELL_PADDING_DEFAULT;
  const pr = padding?.right ?? TABLE_CELL_PADDING_DEFAULT;
  const pb = padding?.bottom ?? TABLE_CELL_PADDING_DEFAULT;
  const pl = padding?.left ?? TABLE_CELL_PADDING_DEFAULT;
  const preset = el.data.borderPreset ?? 'full';
  const stroke = firstStroke(el.appearance);
  const borderW = stroke?.width ?? TABLE_BORDER_WIDTH;
  const borderColor = stroke?.color ?? TABLE_BORDER_COLOR;
  const borderStyle = stroke?.style ?? 'solid';
  const xPositions = [x];
  const yPositions = [y];
  for (const colW of colWidths) xPositions.push(xPositions[xPositions.length - 1]! + colW);
  for (const rowH of rowHeights) yPositions.push(yPositions[yPositions.length - 1]! + rowH);

  const setLineStyle = () => {
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderW;
    if (borderStyle === 'dashed') ctx.setLineDash([borderW * 3, borderW * 2]);
    else if (borderStyle === 'dotted') ctx.setLineDash([borderW, borderW]);
    else ctx.setLineDash([]);
  };

  const strokeLine = (x1: number, y1: number, x2: number, y2: number) => {
    if (borderW <= 0) return;
    setLineStyle();
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  let cy = y;
  for (let r = 0; r < rows; r++) {
    let cx = x;
    for (let c = 0; c < cols; c++) {
      const merged = getMergedInfo(el, r, c);
      if (merged === null) {
        cx += colWidths[c] ?? 0;
        continue;
      }
      const { rowSpan, colSpan, originR, originC } = merged;
      const cell = el.data.cells?.[`${originR},${originC}`];
      const w = xPositions[originC + colSpan]! - xPositions[originC]!;
      const h = yPositions[originR + rowSpan]! - yPositions[originR]!;
      const cellX = xPositions[originC]!;
      const cellY = yPositions[originR]!;
      const bg = getCellBackground(el, originR, originC);
      ctx.fillStyle = bg;
      ctx.fillRect(cellX, cellY, w, h);

      const touchesTop = originR === 0;
      const touchesLeft = originC === 0;
      const touchesRight = originC + colSpan === cols;
      const touchesBottom = originR + rowSpan === rows;
      if (preset === 'full') {
        strokeLine(cellX, cellY, cellX + w, cellY);
        strokeLine(cellX, cellY, cellX, cellY + h);
        strokeLine(cellX + w, cellY, cellX + w, cellY + h);
        strokeLine(cellX, cellY + h, cellX + w, cellY + h);
      } else if (preset === 'outerOnly') {
        if (touchesTop) strokeLine(cellX, cellY, cellX + w, cellY);
        if (touchesLeft) strokeLine(cellX, cellY, cellX, cellY + h);
        if (touchesRight) strokeLine(cellX + w, cellY, cellX + w, cellY + h);
        if (touchesBottom) strokeLine(cellX, cellY + h, cellX + w, cellY + h);
      } else if (preset === 'outerInnerH') {
        if (touchesTop) strokeLine(cellX, cellY, cellX + w, cellY);
        if (touchesLeft) strokeLine(cellX, cellY, cellX, cellY + h);
        if (touchesRight) strokeLine(cellX + w, cellY, cellX + w, cellY + h);
        strokeLine(cellX, cellY + h, cellX + w, cellY + h);
      } else if (preset === 'outerHInnerH') {
        if (touchesTop) strokeLine(cellX, cellY, cellX + w, cellY);
        strokeLine(cellX, cellY + h, cellX + w, cellY + h);
      } else if (preset === 'outerInnerV') {
        if (touchesTop) strokeLine(cellX, cellY, cellX + w, cellY);
        if (touchesLeft) strokeLine(cellX, cellY, cellX, cellY + h);
        strokeLine(cellX + w, cellY, cellX + w, cellY + h);
        if (touchesBottom) strokeLine(cellX, cellY + h, cellX + w, cellY + h);
      } else if (preset === 'outerVInnerV') {
        if (touchesLeft) strokeLine(cellX, cellY, cellX, cellY + h);
        strokeLine(cellX + w, cellY, cellX + w, cellY + h);
      }

      const text = cellText(cell);
      if (text) {
        const textStyle = getCellTextStyle(el, originR, originC);
        ctx.save();
        ctx.beginPath();
        ctx.rect(cellX + pl, cellY + pt, Math.max(0, w - pl - pr), Math.max(0, h - pt - pb));
        ctx.clip();
        ctx.font = fontCss(textStyle);
        ctx.fillStyle = resolveCellTextColor(el, cell);
        const rect = { x: cellX, y: cellY, width: w, height: h };
        const cellPadding = { top: pt, right: pr, bottom: pb, left: pl };
        if (textStyle.writingMode === 'vertical-rl') {
          drawVerticalCellText(ctx, text, textStyle, rect, cellPadding);
        } else {
          drawHorizontalCellText(ctx, text, textStyle, rect, cellPadding);
        }
        ctx.restore();
      }
      cx += colWidths[c] ?? 0;
    }
    cy += rowHeights[r];
  }
}
